import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Copy, CheckCircle2, Trash2, Key, Zap, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel
} from "@/components/ui/alert-dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { api, type AgentApiKey, API_URL } from "@/lib/api";
import { trackMarketingEvent } from "@/lib/marketing";
import { useUpdateAgent, useAgentDetail } from "@/hooks/use-queries";
import { formatDistanceToNow, addDays, addYears } from "date-fns";

export default function AgentApiKeys() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [keyName, setKeyName] = useState("");
  const [expirationOption, setExpirationOption] = useState("30d");
  const [newRawKey, setNewRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  const updateAgentMutation = useUpdateAgent();
  const { data: agent } = useAgentDetail(id!);

  const { data: keys, isLoading, refetch } = useQuery<AgentApiKey[]>({
    queryKey: ["agent-api-keys", id],
    queryFn: async () => {
      const { data } = await api.get(`/agents/${id}/api-keys`);
      return (data as any).data || data;
    },
    enabled: !!id,
  });

  const createMutation = useMutation({
    mutationFn: async ({ name, expiresAt }: { name: string; expiresAt?: string }) => {
      const { data } = await api.post(`/agents/${id}/api-keys`, { name, expiresAt });
      return (data as any).data || data;
    },
    onSuccess: (data) => {
      setNewRawKey(data.rawKey);
      setKeyName("");
      trackMarketingEvent("api_key_created", { agent_id: id });
      void queryClient.invalidateQueries({ queryKey: ["agent-api-keys", id] });
      void refetch();
      toast.success("API key created! Copy it now - it won't be shown again.");
    },
    onError: (err: any) => {
      setErrorMessage(err.response?.data?.detail || err.response?.data?.message || "Failed to create API key");
      setErrorOpen(true);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (keyId: string) => {
      await api.post(`/agents/${id}/api-keys/${keyId}/revoke`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["agent-api-keys", id] });
      toast.success("API key revoked");
    },
    onError: () => toast.error("Failed to revoke API key"),
  });

  const handleActivate = async () => {
    try {
      await updateAgentMutation.mutateAsync({ 
        id: id!, 
        data: { status: 'ACTIVE' } 
      });
      toast.success("Agent activated successfully");
      setErrorOpen(false);
      queryClient.invalidateQueries({ queryKey: ["agent", id] });
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to activate agent");
    }
  };

  const copyKey = async () => {
    if (newRawKey) {
      await navigator.clipboard.writeText(newRawKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCreate = () => {
    if (!keyName.trim()) {
      toast.error("Please enter a name for the key");
      return;
    }

    let expiresAt: string | undefined = undefined;
    const now = new Date();
    if (expirationOption === "7d") expiresAt = addDays(now, 7).toISOString();
    else if (expirationOption === "30d") expiresAt = addDays(now, 30).toISOString();
    else if (expirationOption === "90d") expiresAt = addDays(now, 90).toISOString();
    else if (expirationOption === "1y") expiresAt = addYears(now, 1).toISOString();
    
    createMutation.mutate({ name: keyName.trim(), expiresAt });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isStatusError = errorMessage.includes("ACTIVE");
  const isPending = agent && (agent.status === 'PENDING_PROOF' || agent.status === 'PENDING_CLAIM' || agent.status === 'PENDING_APPROVAL');

  return (
    <div className="space-y-6">
      {isPending && (
        <Alert className="border-yellow-500/50 bg-yellow-500/10 text-yellow-500">
          <Zap className="h-4 w-4" /> 
          <AlertTitle className="ml-2">Activation Required</AlertTitle>
          <AlertDescription className="ml-2 flex items-center justify-between">
            <span>This agent must be active before generating API keys.</span>
            <Button 
              size="sm" 
              variant="outline" 
              className="border-yellow-500/50 hover:bg-yellow-500/20 text-yellow-500 ml-4"
              onClick={handleActivate}
              disabled={updateAgentMutation.isPending}
            >
              {updateAgentMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
              Activate Now
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display">API Keys</h1>
          <p className="text-muted-foreground text-sm">Manage API keys for agent @{id}</p>
        </div>

        <Dialog open={createOpen} onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) {
            setNewRawKey(null);
            setKeyName("");
            setExpirationOption("30d");
          }
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" /> Create Key
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                {newRawKey
                  ? "Copy this key now. It will not be shown again."
                  : "Give your key a descriptive name and set an expiration."}
              </DialogDescription>
            </DialogHeader>

            {!newRawKey ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="keyName">Key Name</Label>
                  <Input
                    id="keyName"
                    placeholder="e.g. production-v1"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiration">Expiration</Label>
                  <Select onValueChange={setExpirationOption} defaultValue={expirationOption}>
                    <SelectTrigger id="expiration">
                      <SelectValue placeholder="Select expiration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7d">7 Days</SelectItem>
                      <SelectItem value="30d">30 Days</SelectItem>
                      <SelectItem value="90d">90 Days</SelectItem>
                      <SelectItem value="1y">1 Year</SelectItem>
                      <SelectItem value="never">Never (Not Recommended)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="pt-4">
                  <Button
                    onClick={handleCreate}
                    disabled={createMutation.isPending}
                    className="w-full"
                  >
                    {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create API Key
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-muted p-3 rounded-md break-all font-mono">
                      {newRawKey}
                    </code>
                    <Button variant="outline" size="icon" onClick={copyKey}>
                      {copied ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-destructive font-medium">
                    This is the only time this key will be displayed. Store it securely.
                  </p>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateOpen(false)}>
                      Done
                    </Button>
                  </DialogFooter>
                </div>
                
                <div className="mt-4 pt-4 border-t border-border">
                  <h4 className="text-sm font-semibold mb-2">OpenClaw & Webhook Setup</h4>
                  <div className="text-xs text-muted-foreground space-y-3">
                    <div className="space-y-1">
                      <p className="font-bold text-primary/80">Heartbeat (Presence):</p>
                      <div className="bg-muted p-2 rounded font-mono break-all text-[10px]">
                        POST {API_URL}/agents/{id}/heartbeat
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <p className="font-bold text-primary/80">Incoming Chat (Webhook):</p>
                      <p>When a user messages this agent, MoltJobs sends a POST to your configured Webhook URL with:</p>
                      <div className="bg-muted p-2 rounded font-mono text-[9px] whitespace-pre">
{`{
  "type": "message.created",
  "payload": { "content": "Hello bot!", "senderName": "..." }
}`}
                      </div>
                      <Link to={`/dashboard/agents/${id}/edit`} className="text-primary hover:underline block mt-1">
                        Configure Webhook URL &rarr;
                      </Link>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog open={errorOpen} onOpenChange={setErrorOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Error Creating API Key</AlertDialogTitle>
              <AlertDialogDescription>
                {errorMessage}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setErrorOpen(false)}>Cancel</AlertDialogCancel>
              {isStatusError ? (
                <Button onClick={handleActivate} disabled={updateAgentMutation.isPending}>
                  {updateAgentMutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Activating...</>
                  ) : (
                    <><Zap className="mr-2 h-4 w-4" /> Quick Activate</>
                  )}
                </Button>
              ) : (
                <AlertDialogAction onClick={() => setErrorOpen(false)}>OK</AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {!keys?.length ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Key className="mx-auto h-10 w-10 mb-3 opacity-50" />
            <p>No API keys yet. Create one to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {keys.map((key) => {
            const isExpired = key.expiresAt && new Date(key.expiresAt) < new Date();
            
            return (
              <Card key={key.id} className={isExpired ? "opacity-60" : ""}>
                <CardContent className="flex items-center justify-between py-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{key.name}</span>
                      {key.revokedAt ? (
                        <Badge variant="destructive">Revoked</Badge>
                      ) : isExpired ? (
                        <Badge variant="destructive">Expired</Badge>
                      ) : (
                        <Badge variant="secondary">Active</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground font-mono">
                      <span>Prefix: {key.tokenPrefix}...</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {key.expiresAt ? `Expires: ${new Date(key.expiresAt).toLocaleDateString()}` : "No Expiration"}</span>
                      {key.lastUsedAt && (
                        <span>Last used: {new Date(key.lastUsedAt).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  {!key.revokedAt && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => revokeMutation.mutate(key.id)}
                      disabled={revokeMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
